import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';
import { 
  User, Users, GraduationCap, Eye, CheckCircle, 
  Clock, XCircle, LogOut, Loader2, FileText, Shield
} from 'lucide-react';

const ParentPortal = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [parent, setParent] = useState(null);
  const [linkedStudents, setLinkedStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentOverview, setStudentOverview] = useState(null);
  const [loading, setLoading] = useState(false);

  const [credentials, setCredentials] = useState({
    email: '', password: '', first_name: '', last_name: '', student_email: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('parent_token');
    if (token) {
      setIsLoggedIn(true);
      loadParentProfile();
    }
  }, []);

  // Parent uses a separate token
  const getParentConfig = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('parent_token')}` }
  });

  const loadParentProfile = async () => {
    try {
      const response = await api.get('/parent/me', getParentConfig());
      setParent(response.data);
      setLinkedStudents(response.data.linked_students || []);
      if (response.data.linked_students?.length > 0) {
        setSelectedStudent(response.data.linked_students[0]);
        loadStudentOverview(response.data.linked_students[0]._id);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      handleLogout();
    }
  };

  const loadStudentOverview = async (studentId) => {
    setLoading(true);
    try {
      const response = await api.get(`/parent/student/${studentId}/overview`, getParentConfig());
      setStudentOverview(response.data);
    } catch (error) {
      console.error('Error loading student overview:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    try {
      const endpoint = isRegistering ? '/parent/register' : '/parent/login';
      const payload = isRegistering 
        ? credentials 
        : { email: credentials.email, password: credentials.password };
      
      const response = await api.post(endpoint, payload);
      localStorage.setItem('parent_token', response.data.access_token);
      setIsLoggedIn(true);
      
      if (response.data.pending_approval) {
        toast({ 
          title: 'Account Created', 
          description: 'Waiting for student to approve your access.' 
        });
      }
      
      loadParentProfile();
    } catch (error) {
      // Global error handler shows toast
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('parent_token');
    setIsLoggedIn(false);
    setParent(null);
    setLinkedStudents([]);
    setStudentOverview(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'accepted': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'denied': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'pending': return <Clock className="h-4 w-4 text-gray-400" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  // Login/Register Screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-xl border-2 border-blue-100">
          <CardHeader className="text-center">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-4 rounded-2xl w-fit mx-auto mb-4 shadow-lg">
              <Users className="h-10 w-10 text-white" />
            </div>
            <CardTitle className="text-2xl bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Parent Portal
            </CardTitle>
            <CardDescription>
              {isRegistering ? 'Create your parent account' : 'Sign in to view your student\'s progress'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAuth} className="space-y-4">
              {isRegistering && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>First Name</Label>
                      <Input 
                        value={credentials.first_name}
                        onChange={(e) => setCredentials({...credentials, first_name: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label>Last Name</Label>
                      <Input 
                        value={credentials.last_name}
                        onChange={(e) => setCredentials({...credentials, last_name: e.target.value})}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Student's Email</Label>
                    <Input 
                      type="email"
                      value={credentials.student_email}
                      onChange={(e) => setCredentials({...credentials, student_email: e.target.value})}
                      placeholder="Your student's Wayo account email"
                      required
                    />
                  </div>
                </>
              )}
              <div>
                <Label>Your Email</Label>
                <Input 
                  type="email"
                  value={credentials.email}
                  onChange={(e) => setCredentials({...credentials, email: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input 
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-indigo-600">
                {isRegistering ? 'Create Account' : 'Sign In'}
              </Button>
              <p className="text-center text-sm text-gray-600">
                {isRegistering ? 'Already have an account? ' : 'Need an account? '}
                <button 
                  type="button"
                  onClick={() => setIsRegistering(!isRegistering)}
                  className="text-blue-600 hover:underline font-medium"
                >
                  {isRegistering ? 'Sign In' : 'Register'}
                </button>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main Dashboard
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-xl">
              <Users className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Parent Portal</h1>
              <p className="text-sm text-gray-600">View Only Access</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="flex items-center gap-1">
              <Eye className="h-3 w-3" /> Read Only
            </Badge>
            <Button variant="outline" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" /> Sign Out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {linkedStudents.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Clock className="h-12 w-12 text-amber-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Waiting for Approval</h3>
              <p className="text-gray-600">
                Your access request is pending. Your student needs to approve your request from their account.
              </p>
            </CardContent>
          </Card>
        ) : studentOverview ? (
          <>
            {/* Student Info */}
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-100 p-3 rounded-full">
                      <GraduationCap className="h-8 w-8 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold">
                        {studentOverview.student.first_name} {studentOverview.student.last_name}
                      </h2>
                      <p className="text-gray-600">{studentOverview.student.email}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-gray-900">{studentOverview.stats.total_applications}</p>
                  <p className="text-sm text-gray-600">Applications</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">{studentOverview.stats.submitted}</p>
                  <p className="text-sm text-gray-600">Submitted</p>
                </CardContent>
              </Card>
              <Card className="bg-green-50">
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{studentOverview.stats.accepted}</p>
                  <p className="text-sm text-green-700">Accepted</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <p className="text-3xl font-bold text-gray-600">{studentOverview.stats.pending_decisions}</p>
                  <p className="text-sm text-gray-600">Pending</p>
                </CardContent>
              </Card>
            </div>

            {/* Applications List */}
            <Card>
              <CardHeader>
                <CardTitle>Applications</CardTitle>
                <CardDescription>Your student's college applications</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {studentOverview.applications.map((app) => (
                    <div key={app.application_id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        {getStatusIcon(app.decision_status)}
                        <div>
                          <p className="font-medium">{app.college_name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">{app.deadline_type}</Badge>
                            <Badge variant="outline" className="text-xs">{app.status}</Badge>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <Progress value={app.progress} className="w-24 h-2" />
                        <p className="text-xs text-gray-500 mt-1">{app.progress}% complete</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentPortal;
